import React from 'react'
import PropTypes from 'prop-types'

import { pick, isEqual, isEmpty } from 'lodash'

import { Box, Button, Typography, TextField } from '@material-ui/core'

import { ConfirmationDialog } from '@scoir/modals'

import GenerateSftpKey from './GenerateSftpKey'

const PROP_FIELDS = ['username', 'password', 'publicKey', 'hasStoredPassword']
const TEMPORARY_PASSWORD = 'XXtemporaryXX'

const generateState = (props) => {
  const defaultPassword = props.hasStoredPassword ? TEMPORARY_PASSWORD : ''
  const state = {
    original: pick(props, PROP_FIELDS),
    username: props.username || '',
    password: props.password || defaultPassword,
    publicKey: props.publicKey || '',
    hasStoredPassword: props.hasStoredPassword || false,
    promptForClear: false,
  }
  state.original.password = state.original.password || defaultPassword
  return state
}

const hasEqualPropFields = (obj1, obj2) => {
  return isEqual(pick(obj1, PROP_FIELDS), pick(obj2, PROP_FIELDS))
}

class CredentialsStep extends React.Component {
  constructor(props) {
    super(props)
    this.state = generateState(props)
  }

  componentDidUpdate(prevProps) {
    if (!hasEqualPropFields(prevProps, this.props)) {
      this.setState(generateState(this.props))
    }
  }

  handleChange(name, val) {
    const newState = {
      ...this.state,
      [name]: val,
    }
    this.setState(newState)
    this.props.onModificationChange(!hasEqualPropFields(newState, newState.original))
  }

  handleChangeEvt(e) {
    this.handleChange(e.target.name, e.target.value)
  }

  validatePassword(val) {
    if (val === '' && this.state.hasStoredPassword) {
      this.handleChange('password', TEMPORARY_PASSWORD)
    }
  }

  clearTemporaryPassword() {
    if (this.state.password === TEMPORARY_PASSWORD) {
      this.handleChange('password', '')
    }
  }

  promptForClear() {
    this.setState({
      promptForClear: true,
    })
  }

  savePassword() {
    this.props.onSavePassword(this.state.username, this.state.password)
  }

  render() {
    const { editable } = this.props
    return (
      <Box
        bgcolor={editable ? 'background.paper' : 'grey.100'}
        borderRadius="borderRadius"
        p={2}
        mb={2}
      >
        <Typography
          variant="h6"
          component="h5"
          gutterBottom
        >
          Step 2: Enter Server Credentials
        </Typography>
        <Typography gutterBottom>
          These credentials will be used to gain access to your SFTP server for file delivery.
        </Typography>

        <Box my={1}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={!editable}
            onClick={() => this.promptForClear()}
          >
            Clear Credentials
          </Button>
        </Box>

        <ConfirmationDialog
          open={this.state.promptForClear}
          headerText="Clear Credentials"
          onClose={() => this.setState({ promptForClear: false })}
          onConfirm={() => {
            this.setState({ promptForClear: false })
            this.props.onClearCredentials()
          }}
        >
          <Typography>Are you sure you want to clear your SFTP credentials?</Typography>
        </ConfirmationDialog>

        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          color="primary"
          name="username"
          label="Username"
          required
          value={this.state.username}
          onChange={(e) => this.handleChangeEvt(e)}
          disabled={!editable}
        />

        <Box mt={2}>
          <Typography>
            Provide a <em>password</em> or generate a <em>public key</em> to access your SFTP server.
          </Typography>
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="center"
          mt={3}
        >
          <Box width="100%">
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              color="primary"
              type="password"
              name="password"
              label="Password"
              disabled={isEmpty(this.state.username) || !isEmpty(this.state.publicKey)}
              required={isEmpty(this.state.publicKey)}
              value={this.state.password}
              onChange={(e) => {
                this.clearTemporaryPassword()
                this.handleChangeEvt(e)
                this.validatePassword(e.target.value)
              }}
            />

            <Box mt={3}>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  isEmpty(this.state.username) ||
                  isEmpty(this.state.password) ||
                  !isEmpty(this.state.publicKey) ||
                  this.state.password === TEMPORARY_PASSWORD
                }
                onClick={() => this.savePassword()}
              >
                Save
              </Button>
            </Box>
          </Box>
          <Box
            my={{ xs: 2, md: 0 }}
            mx={{ xs: 0, md: 2 }}
          >
            <Typography>
              <strong>Or</strong>
            </Typography>
          </Box>
          <Box width="100%">
            <GenerateSftpKey
              scid={this.props.scid}
              publicKey={this.state.publicKey}
              onKeyUpdate={() => this.props.onKeyChange(this.state.username)}
              disabled={!editable}
            />
          </Box>
        </Box>
      </Box>
    )
  }
}

CredentialsStep.propTypes = {
  scid: PropTypes.number.isRequired,
  editable: PropTypes.bool.isRequired,
  username: PropTypes.string,
  password: PropTypes.string,
  publicKey: PropTypes.string,
  hasStoredPassword: PropTypes.bool,
  disabled: PropTypes.bool,
  onClearCredentials: PropTypes.func.isRequired,
  onSavePassword: PropTypes.func.isRequired,
  onKeyChange: PropTypes.func.isRequired,
  onModificationChange: PropTypes.func.isRequired,
}

export default CredentialsStep
